import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getUserProfile, listUserProfiles, upsertUserProfile } from '../lib/firestore';

function toLoginId(email = '', uid = '') {
  const raw = String(email || '').trim();
  if (raw.includes('@')) return raw.split('@')[0];
  return raw || String(uid || '').trim() || '-';
}

export default function AdminSiteAdminsPage() {
  const { user, isAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUid, setSelectedUid] = useState('');
  const [selectedProfile, setSelectedProfile] = useState({
    name: '',
    contact: '',
    position: '',
    email: '',
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  async function loadAdmins() {
    setLoading(true);
    setError('');
    try {
      const rows = await listUserProfiles({ role: 'site_admin' });
      setAdmins(rows);
      setSelectedUid((prev) => (prev && rows.some((row) => row.uid === prev) ? prev : ''));
    } catch (err) {
      setError(`サイト管理者一覧の読み込みに失敗しました。${err?.message ? ` ${err.message}` : ''}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAdmins();
  }, []);

  useEffect(() => {
    async function loadSelectedAdmin() {
      if (!selectedUid) return;
      const row = admins.find((item) => item.uid === selectedUid);
      const profile = row || await getUserProfile(selectedUid);
      if (!profile) return;
      setSelectedProfile({
        name: profile.name || '',
        contact: profile.contact || '',
        position: profile.position || '',
        email: profile.email || '',
      });
    }
    loadSelectedAdmin();
  }, [admins, selectedUid]);

  const selectedRow = useMemo(
    () => admins.find((row) => row.uid === selectedUid) || null,
    [admins, selectedUid]
  );

  async function handleSubmit(event) {
    event.preventDefault();
    setFeedback({ type: '', message: '' });
    if (!isAdmin) {
      setFeedback({ type: 'error', message: 'この操作はサイト管理者のみ実行できます。' });
      return;
    }
    if (!selectedUid) {
      setFeedback({ type: 'error', message: '編集対象のサイト管理者を選択してください。' });
      return;
    }

    setSaving(true);
    try {
      await upsertUserProfile(selectedUid, {
        name: selectedProfile.name,
        contact: selectedProfile.contact,
        position: selectedProfile.position,
      });
      await loadAdmins();
      setFeedback({ type: 'success', message: 'サイト管理者情報を更新しました。' });
    } catch (err) {
      setFeedback({ type: 'error', message: `更新に失敗しました。${err?.message ? ` ${err.message}` : ''}` });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Helmet><title>Drs.Farm | サイト管理者管理</title></Helmet>
      <h1>サイト管理者管理</h1>
      <p className="hint">サイト管理者アカウントの一覧確認、連絡先や役職の更新を行います。</p>
      {error ? <p className="error-text">{error}</p> : null}

      <section className="panel">
        <div className="row-actions">
          <Link className="btn btn-secondary" to="/admin">管理者メニューへ戻る</Link>
          <Link className="btn btn-secondary" to="/admin/accounts?tab=create">新しいサイト管理者を作成</Link>
          <Link className="btn btn-secondary" to="/admin/settings?tab=notify">承認通知メール設定</Link>
          <button className="btn btn-outline-solid" type="button" onClick={loadAdmins} disabled={loading}>
            {loading ? '更新中...' : '再読み込み'}
          </button>
        </div>
      </section>

      <section className="admin-grid user-edit-grid">
        <article className="panel">
          <div className="directory-head">
            <div>
              <h2>サイト管理者一覧</h2>
              <p className="hint">カードを選ぶと右側で詳細を編集できます。</p>
            </div>
            <p className="directory-count">{admins.length}件</p>
          </div>
          {loading ? <p>読み込み中...</p> : admins.length === 0 ? <p>サイト管理者アカウントはありません。</p> : (
            <ul className="directory-list">
              {admins.map((row) => (
                <li key={row.uid}>
                  <button
                    type="button"
                    className={`directory-row user-select-card ${selectedUid === row.uid ? 'is-active' : ''}`}
                    onClick={() => {
                      setSelectedUid(row.uid);
                      setFeedback({ type: '', message: '' });
                    }}
                  >
                    <div className="site-admin-card-head">
                      <p><strong>{row.name || '名称未設定'}</strong></p>
                      {user?.uid === row.uid ? <span className="site-admin-badge">あなた</span> : null}
                    </div>
                    <p className="directory-meta">ID: {toLoginId(row.email, row.uid)}</p>
                    <p className="directory-meta">メール: {row.email || '-'}</p>
                    <p className="directory-meta">連絡先: {row.contact || '未設定'} / 役職: {row.position || '未設定'}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="panel">
          <h2>サイト管理者情報の編集</h2>
          {selectedUid ? (
            <form className="form-grid" onSubmit={handleSubmit}>
              <div className="site-admin-summary">
                <p className="hint">ログインID</p>
                <p><strong>{toLoginId(selectedProfile.email, selectedUid)}</strong></p>
              </div>
              <label>メールアドレス
                <input value={selectedProfile.email} readOnly />
              </label>
              <label>権限
                <input value="サイト管理者" readOnly />
              </label>
              <label>氏名
                <input
                  value={selectedProfile.name}
                  onChange={(e) => setSelectedProfile((prev) => ({ ...prev, name: e.target.value }))}
                />
              </label>
              <label>連絡先
                <input
                  value={selectedProfile.contact}
                  onChange={(e) => setSelectedProfile((prev) => ({ ...prev, contact: e.target.value }))}
                />
              </label>
              <label>役職
                <input
                  value={selectedProfile.position}
                  onChange={(e) => setSelectedProfile((prev) => ({ ...prev, position: e.target.value }))}
                />
              </label>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? '更新中...' : 'サイト管理者情報を更新'}
              </button>
              {feedback.message ? (
                <p className={feedback.type === 'success' ? 'success-text' : 'error-text'}>
                  {feedback.message}
                </p>
              ) : null}
            </form>
          ) : (
            <div className="form-grid">
              <p className="hint">左のサイト管理者カードを選択してください。</p>
              {selectedRow === null && admins.length > 0 ? (
                <p className="hint">一覧から選ぶと、連絡先や役職をここで更新できます。</p>
              ) : null}
            </div>
          )}
        </article>
      </section>
    </>
  );
}
